"use client";

import { useCallback, useEffect, useState, useTransition } from "react";
import { cn } from "@/lib/utils";
import { Bell } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  getUnreadCount,
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "@/actions/notifications";
import type { ChatNotification } from "@/types/chat";
import Link from "next/link";

const THREAD_LABELS: Record<string, string> = {
  appointment: "Запись",
  task: "Задача",
  work_item: "Работа",
};

function threadHref(n: ChatNotification): string {
  if (n.thread_type === "appointment") return `/appointments/${n.thread_id}`;
  if (n.thread_type === "task") return `/schedule`;
  return `/schedule`;
}

interface NotificationBellProps {
  userId: string;
  className?: string;
}

export default function NotificationBell({ userId, className }: NotificationBellProps) {
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState<ChatNotification[]>([]);
  const [open, setOpen] = useState(false);
  const [isPending, startTransition] = useTransition();

  const refreshCount = useCallback(() => {
    startTransition(async () => {
      try {
        const count = await getUnreadCount(userId);
        setUnread(count);
      } catch {
        // silently ignore — notification count is cosmetic
      }
    });
  }, [userId]);

  // Initial load
  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  // Poll for new notifications
  useEffect(() => {
    const interval = setInterval(refreshCount, 10000);
    return () => clearInterval(interval);
  }, [refreshCount]);

  // Load full list when popover opens
  useEffect(() => {
    if (!open) return;
    startTransition(async () => {
      try {
        const notifs = await listNotifications(userId, false, 30);
        setNotifications(notifs);
      } catch {
        // silently ignore
      }
    });
  }, [open, userId]);

  async function handleMarkRead(id: string) {
    try {
      await markNotificationRead(id);
    } catch {
      // best-effort
    }
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    setUnread((prev) => Math.max(0, prev - 1));
  }

  async function handleMarkAllRead() {
    try {
      await markAllNotificationsRead(userId);
    } catch {
      // best-effort
    }
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("relative", className)}
        >
          <Bell className="size-4" />
          {unread > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 size-5 text-[10px] p-0 flex items-center justify-center"
            >
              {unread > 99 ? "99+" : unread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-2.5 border-b">
          <span className="text-sm font-semibold">Уведомления</span>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={handleMarkAllRead}
              disabled={isPending}
            >
              Прочитать все
            </Button>
          )}
        </div>

        <div className="max-h-72 overflow-y-auto">
          {notifications.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-6">
              Нет уведомлений
            </p>
          )}
          {notifications.map((n) => (
            <Link
              key={n.id}
              href={threadHref(n)}
              onClick={() => {
                if (!n.read) handleMarkRead(n.id);
                setOpen(false);
              }}
              className={cn(
                "block px-4 py-2.5 border-b last:border-b-0 hover:bg-muted/60 transition-colors",
                !n.read && "bg-blue-50/50 dark:bg-blue-950/20",
              )}
            >
              <div className="flex items-center gap-1.5">
                {!n.read && (
                  <span className="size-1.5 rounded-full bg-blue-500 shrink-0" />
                )}
                <span className="text-xs font-medium">{n.from_user_name}</span>
                <span className="text-[10px] text-muted-foreground">
                  · {THREAD_LABELS[n.thread_type] ?? n.thread_type}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                {n.preview}
              </p>
              <span className="text-[10px] text-muted-foreground/60">
                {new Date(n.created_at).toLocaleString("ru-RU", {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
